import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Star, MapPin, ArrowUpRight, Plus, Check } from "lucide-react";

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase() || "?";
}

export function ProviderLogo({ provider, size = 48 }) {
  const has = !!provider?.logo_url;
  return has ? (
    <img
      src={provider.logo_url}
      alt={`${provider.name} logo`}
      className="object-contain bg-white border border-slate-200 rounded-md"
      style={{ width: size, height: size }}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
    />
  ) : (
    <div
      className="grid place-items-center bg-slate-100 border border-slate-200 rounded-md text-slate-500 font-display font-bold"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials(provider?.name)}
    </div>
  );
}

export function VerifiedBadge() {
  return (
    <span className="tag tag-verified" data-testid="badge-verified">
      <ShieldCheck size={11} className="mr-1" /> Verified
    </span>
  );
}

export function FeaturedBadge() {
  return (
    <span className="tag tag-featured" data-testid="badge-featured">
      <Star size={11} className="mr-1 fill-current" /> Featured
    </span>
  );
}

export function TierBadge() {
  return null;
}

export default function ProviderCard({ provider, onCompare, isComparing, onRequestQuote }) {
  // Public badge display is driven by admin-controlled toggle fields,
  // NOT the internal verified/featured flags.
  const showVerifiedBadge = provider.show_verified_badge === true;
  const showFeaturedBadge = provider.show_featured_badge === true;
  const canRequestQuote = provider.enable_quote_button === true;
  const techBadges = [
    ...(provider.connectivity || []).slice(0, 3),
    ...(provider.sensors || []).slice(0, 2),
  ].slice(0, 4);

  return (
    <div className="card-elevated p-5 flex flex-col" data-testid={`provider-card-${provider.slug}`}>
      <div className="flex items-start gap-3">
        <ProviderLogo provider={provider} size={52} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/solutions/${provider.slug}`}
                  className="font-display font-bold text-slate-900 text-base leading-tight hover:underline truncate">
              {provider.name}
            </Link>
            {showVerifiedBadge && <VerifiedBadge />}
            {showFeaturedBadge && <FeaturedBadge />}
            <TierBadge tier={provider.tier} />
          </div>
          {provider.hq_country && (
            <div className="mt-1 inline-flex items-center text-xs text-slate-500 gap-1">
              <MapPin size={12} /> {[provider.hq_city, provider.hq_country].filter(Boolean).join(", ")}
            </div>
          )}
        </div>
      </div>

      <p className="mt-3 text-sm text-slate-600 line-clamp-3">
        {provider.tagline || provider.summary || provider.description?.slice(0, 180) || "Tracking solution provider."}
      </p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {(provider.provider_types || []).slice(0, 1).map((t) => (
          <span key={t} className="tag tag-tech">{t}</span>
        ))}
        {(provider.solution_categories || []).slice(0, 2).map((c) => (
          <span key={c} className="tag">{c}</span>
        ))}
        {techBadges.map((b) => (
          <span key={b} className="tag font-mono text-[10px]">{b}</span>
        ))}
      </div>

      <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
        <Link to={`/solutions/${provider.slug}`}
              data-testid={`view-profile-${provider.slug}`}
              className="btn-secondary text-xs px-3 py-2">
          View profile <ArrowUpRight size={13} />
        </Link>
        <button
          type="button"
          data-testid={`compare-${provider.slug}`}
          onClick={() => onCompare?.(provider)}
          className={`text-xs px-3 py-2 rounded-md border font-semibold inline-flex items-center gap-1 transition-colors ${
            isComparing ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
        >
          {isComparing ? <><Check size={13}/> Comparing</> : <><Plus size={13}/> Compare</>}
        </button>
        {canRequestQuote && (
          <button
            type="button"
            data-testid={`request-quote-${provider.slug}`}
            onClick={() => onRequestQuote?.(provider)}
            className="btn-accent text-xs px-3 py-2"
          >
            Request quote
          </button>
        )}
      </div>
    </div>
  );
}
